import { createApi } from '@reduxjs/toolkit/query/react';

import { IExerciseOption } from '../../types/interfaces/exercise-option';
import { IExerciseProperty } from '../../types/interfaces/exercise-property';
import { API_CACHE_KEYS, ApiResponseCache } from '../api-response-cache';
import { EXERCISE_CONFIG, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

const transformErrorWithNotification =
  TransformedErrorResponseWithNotification().transformErrorResponse;

export const exercisesConfigApi = createApi({
  reducerPath: 'exerciseConfigApi',
  tagTypes: [
    EXERCISE_CONFIG.EXERCISE_CATEGORIES,
    EXERCISE_CONFIG.EXERCISE_OPTIONS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getExerciseOptionsApi: builder.query<IExerciseOption[], void>({
      keepUnusedDataFor: 3600,
      serializeQueryArgs: ({ endpointName }) =>
        `${endpointName}-exercise-options`,
      query() {
        return {
          url: endpoints.ExerciseOption.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_CONFIG.EXERCISE_OPTIONS),
      transformResponse: (response: any) => {
        const array = response;
        array.sort((a, b) => a.priorityOrder - b.priorityOrder);
        ApiResponseCache.set(API_CACHE_KEYS.EXERCISE_OPTIONS, array);

        return array;
      },
      ...TransformedErrorResponseWithNotification(),
      transformErrorResponse(
        response: { status?: number | string },
        meta: unknown,
      ) {
        if (response.status !== 'FETCH_ERROR') {
          ApiResponseCache.remove(API_CACHE_KEYS.EXERCISE_OPTIONS);
        }
        return transformErrorWithNotification(response, meta);
      },
    }),
    getExercisePropertiesApi: builder.query<IExerciseProperty[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.ExerciseProperties.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_CONFIG.EXERCISE_CATEGORIES),
      transformResponse: (response: any) => {
        const array = response;
        const getName = (item: IExerciseProperty) => item.name?.trim() ?? '';
        const isHighPriority = (item: IExerciseProperty) =>
          (item.priority ?? 0) > 9000;

        array.sort((a, b) => {
          const aIsHighPriority = isHighPriority(a);
          const bIsHighPriority = isHighPriority(b);

          if (aIsHighPriority !== bIsHighPriority) {
            return aIsHighPriority ? 1 : -1;
          }

          if (aIsHighPriority && bIsHighPriority) {
            const priorityDiff = (a.priority ?? 0) - (b.priority ?? 0);
            if (priorityDiff !== 0) {
              return priorityDiff;
            }
          }

          return getName(a).localeCompare(getName(b), undefined, {
            sensitivity: 'base',
          });
        });

        return array;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetExerciseOptionsApiQuery,
  useLazyGetExerciseOptionsApiQuery: useLazyGetExerciseOptions,
  useGetExercisePropertiesApiQuery: useGetExerciseProperties,
} = exercisesConfigApi;

export const resetExerciseConfigApiState = () =>
  exercisesConfigApi.util.resetApiState();

export const useGetExerciseOptions = () => {
  const result = useGetExerciseOptionsApiQuery(undefined);
  const cached = ApiResponseCache.get<IExerciseOption[]>(
    API_CACHE_KEYS.EXERCISE_OPTIONS,
  );

  return {
    ...result,
    isPreloadedCached: !result.data && !!cached,
    data: result.data ?? cached ?? undefined,
  };
};
